import { useState, useMemo, useEffect, useRef } from "react";
import { Input } from "@/common/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { Card, CardContent } from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import { Slider } from "@mui/material";
import {
  Search,
  Filter,
  X,
  PieChart,
  BarChart4,
  DollarSign,
  ChevronRight,
  ChevronLeft,
  Activity,
  TrendingUp,
  Building2,
  Briefcase,
} from "lucide-react";
import ProductLayout from "@/projects/IPO/components/ProductLayout";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TableColumnsType } from "antd";
import { AntDTable } from "@/common/components/ui";
import ScreenerService from "@/services/Screener/APIs/ScreenerService";
import { ScreenerItem } from "@/utils/types/Screener/Screener";
import LoadingSpinner from "@/common/components/common/LoadingSpinner";
import screenerNavigationRoutes from "../../utils/navigationRoutes";

// Company type definition mapped from API
interface Company {
  id: string;
  name: string;
  ticker: string;
  sector: string;
  price: number;
  change: number;
  marketCap: number;
  peRatio: number;
}

// Map API response to Company format
const mapScreenerItemToCompany = (
  item: ScreenerItem,
  index: number
): Company => ({
  id: item.co_code?.toString() || index.toString(),
  name: item.companyname || item.companyshortname || "",
  ticker: item.nsesymbol || item.bsecode || "",
  sector: item.sectorname || item.industryname || "N/A",
  price: 0, // Not available in API response
  change: 0, // Not available in API response
  marketCap: parseFloat(item.mcap) || 0,
  peRatio: 0, // Not available in API response
});

const ScreenerHomePage = () => {
  const navigate = useNavigate();
  const tableRef = useRef<HTMLDivElement>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [marketCapFilter, setMarketCapFilter] = useState("all");
  const [peRange, setPeRange] = useState<number[]>([0, 100]);
  const [showFilters, setShowFilters] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Fetch companies from API
  const fetchCompanies = async (page: number) => {
    setLoading(true);
    try {
      const response = await ScreenerService.getCompanyMaster({
        page,
        page_size: itemsPerPage,
      });

      if (response?.success && response?.data) {
        const mappedCompanies = response.data.results.map((item, index) =>
          mapScreenerItemToCompany(item, (page - 1) * itemsPerPage + index)
        );
        setCompanies(mappedCompanies);
        setTotalCount(response.data.count);
      } else {
        setCompanies([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when page changes
  useEffect(() => {
    fetchCompanies(currentPage);
  }, [currentPage]);

  // Scroll to top when page changes and data is loaded
  useEffect(() => {
    if (!loading && companies.length > 0) {
      // Scroll to absolute top after a small delay to ensure DOM is fully updated
      const scrollTimer = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 150);

      return () => clearTimeout(scrollTimer);
    }
  }, [loading, currentPage, companies.length]);

  // Extract unique sectors from all companies (if needed for filters)
  const sectors = useMemo(
    () => ["all", ...Array.from(new Set(companies.map(c => c.sector)))].sort(),
    [companies]
  );

  const clearFilters = () => {
    setSearchTerm("");
    setSectorFilter("all");
    setMarketCapFilter("all");
    setPeRange([0, 100]);
  };

  // Client-side filtering (can be moved to server-side if API supports it)
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const matchesSearch =
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.ticker.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSector =
        sectorFilter === "all" || company.sector === sectorFilter;

      let matchesMarketCap = true;
      if (marketCapFilter === "large") {
        matchesMarketCap = company.marketCap > 200000;
      } else if (marketCapFilter === "mid") {
        matchesMarketCap =
          company.marketCap > 50000 && company.marketCap <= 200000;
      } else if (marketCapFilter === "small") {
        matchesMarketCap = company.marketCap <= 50000;
      }

      const matchesPe =
        company.peRatio >= peRange[0] && company.peRatio <= peRange[1];

      return matchesSearch && matchesSector && matchesMarketCap && matchesPe;
    });
  }, [companies, searchTerm, sectorFilter, marketCapFilter, peRange]);

  // Pagination Logic
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to absolute top immediately when page changes
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  };

  // Generate pagination page numbers with ellipsis
  const getPaginationPages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 7; // Show max 7 page numbers including ellipsis

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    if (currentPage <= 4) {
      // Show first 4 pages, then ellipsis, then last page
      for (let i = 2; i <= 4; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      // Show first page, ellipsis, then last 4 pages
      pages.push("...");
      for (let i = totalPages - 3; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, ellipsis, current-1, current, current+1, ellipsis, last page
      pages.push("...");
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  // Ant Design Table Columns Configuration
  const columns: TableColumnsType<Company> = useMemo(
    () => [
      {
        title: (
          <span className="w-[80px] py-4 pl-6 pr-4 font-sans text-xs font-bold text-gray-500">
            Rank
          </span>
        ),
        key: "rank",
        width: 80,
        render: (_: any, __: Company, index: number) => (
          <span className="py-2 pl-6 font-sans text-sm font-medium text-gray-600">
            {(currentPage - 1) * itemsPerPage + index + 1}
          </span>
        ),
      },
      {
        title: (
          <span className="py-4 pr-4 font-sans text-xs font-bold text-gray-500">
            Company
          </span>
        ),
        key: "company",
        render: (_: any, company: Company) => (
          <div className="flex items-center gap-3 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-slate-100 font-sans text-[13px] font-bold text-slate-400">
              <Building2 className="h-4 w-4" />
            </div>
            <div className="flex flex-col">
              <span className="font-sans text-sm font-bold text-gray-900 transition-colors group-hover:text-black">
                {company.name}
              </span>
              <span className="font-sans text-xs text-gray-500">
                {company.ticker}
              </span>
            </div>
          </div>
        ),
      },
      {
        title: (
          <span className="pr-4 font-sans text-xs font-bold text-gray-500">
            Sector
          </span>
        ),
        key: "sector",
        render: (_: any, company: Company) => (
          <span className="font-sans text-sm font-medium text-gray-900">
            {company.sector}
          </span>
        ),
      },
      {
        title: (
          <span className="pr-4 text-right font-sans text-xs font-bold text-gray-500">
            Market Cap
          </span>
        ),
        key: "marketCap",
        align: "right",
        render: (_: any, company: Company) => (
          <span className="font-sans text-sm font-medium text-gray-900">
            ₹{company.marketCap.toLocaleString()} Cr
          </span>
        ),
      },
      {
        title: "",
        key: "action",
        align: "right",
        width: 80,
        render: () => (
          <div className="pr-6 text-right">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-md text-slate-300 group-hover:text-slate-600"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [currentPage, itemsPerPage]
  );

  // Stats calculations (simplified since we don't have all data)
  const indexMktCap = useMemo(
    () => companies.reduce((acc, curr) => acc + (curr.marketCap || 0), 0),
    [companies]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-[#fcfcfc]" ref={tableRef}>
        <div className="container relative mx-auto px-4 py-10 md:px-6">
          {/* Professional Header Section */}
          <div className="mb-10 flex flex-col items-end justify-between gap-6 border-b border-slate-100 pb-8 lg:flex-row">
            <div>
              <div className="mb-2 flex items-center gap-2 text-slate-400">
                <Activity className="h-4 w-4" />
                <span className="text-[13px] font-bold uppercase tracking-widest">
                  Market Intelligence
                </span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-slate-800">
                Equity Screener
              </h1>
              <p className="mt-1 text-base text-slate-500">
                Institutional-grade stock analysis and filtering.
              </p>
            </div>

            <div className="flex w-full flex-wrap items-center gap-3 lg:w-auto">
              <div className="relative flex-1 lg:w-[400px]">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by company or ticker..."
                  className="h-11 rounded-lg border-slate-200 bg-white pl-10 shadow-sm focus:ring-slate-400"
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                />
              </div>
              <Button
                variant={showFilters ? "secondary" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
                className="h-11 gap-2 rounded-lg border-slate-200 px-5 text-base font-semibold"
              >
                <Filter className="h-4 w-4" />
                {showFilters ? "Hide Filters" : "Filters"}
              </Button>
            </div>
          </div>

          {/* Clean Stats Strip */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: "Total Companies",
                value: totalCount.toLocaleString(),
                change: "Active Listings",
                color: "text-slate-800",
                icon: Briefcase,
              },
              {
                label: "Aggregate Cap",
                value: `₹${(indexMktCap / 1000).toFixed(2)}T`,
                change: "Total Market",
                color: "text-slate-800",
                icon: Briefcase,
              },
              {
                label: "Current Page",
                value: `${currentPage}`,
                change: `of ${totalPages}`,
                color: "text-blue-600",
                icon: Activity,
              },
              {
                label: "Records Shown",
                value: `${companies.length}`,
                change: "Per Page",
                color: "text-emerald-600",
                icon: TrendingUp,
              },
            ].map(stat => (
              <Card
                key={stat.label}
                className="overflow-hidden rounded-lg border-slate-100 shadow-sm transition-all hover:border-slate-200"
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div>
                    <p className="mb-1 text-[13px] font-bold uppercase tracking-wider text-slate-400">
                      {stat.label}
                    </p>
                    <h3 className="max-w-[120px] truncate text-base font-bold text-slate-800">
                      {stat.value}
                    </h3>
                    <p className={`text-[14px] font-bold ${stat.color} mt-0.5`}>
                      {stat.change}
                    </p>
                  </div>
                  <div className="rounded-md border border-slate-100 bg-slate-50 p-2">
                    <stat.icon className="h-4 w-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mb-8 overflow-hidden"
              >
                <Card className="rounded-lg border-slate-200 bg-white shadow-md">
                  <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Filter className="h-4 w-4" />
                      <span className="text-xs font-bold uppercase tracking-wider">
                        Analysis Filters
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-8 text-[14px] font-bold uppercase tracking-wider text-slate-400 hover:text-rose-500"
                    >
                      Reset All <X className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                      <div className="space-y-3">
                        <label className="flex items-center gap-2 text-sm font-bold uppercase text-slate-500">
                          <PieChart className="h-3 w-3" /> Sector
                        </label>
                        <Select
                          value={sectorFilter}
                          onValueChange={val => {
                            setSectorFilter(val);
                          }}
                        >
                          <SelectTrigger className="h-10 rounded-md border-slate-200">
                            <SelectValue placeholder="All Sectors" />
                          </SelectTrigger>
                          <SelectContent>
                            {sectors.map(s => (
                              <SelectItem
                                key={s}
                                value={s}
                                className="text-base font-medium"
                              >
                                {s === "all" ? "All Sectors" : s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-3">
                        <label className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500">
                          <DollarSign className="h-3 w-3" /> Market Cap
                        </label>
                        <Select
                          value={marketCapFilter}
                          onValueChange={val => {
                            setMarketCapFilter(val);
                          }}
                        >
                          <SelectTrigger className="h-10 rounded-md border-slate-200">
                            <SelectValue placeholder="All Sizes" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem
                              value="all"
                              className="text-sm font-medium"
                            >
                              All Market Caps
                            </SelectItem>
                            <SelectItem
                              value="large"
                              className="text-sm font-medium"
                            >
                              Large Cap
                            </SelectItem>
                            <SelectItem
                              value="mid"
                              className="text-sm font-medium"
                            >
                              Mid Cap
                            </SelectItem>
                            <SelectItem
                              value="small"
                              className="text-sm font-medium"
                            >
                              Small Cap
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-sm font-bold uppercase text-slate-500">
                            <BarChart4 className="h-3 w-3" /> P/E Ratio
                          </label>
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-[14px] font-bold text-slate-400">
                            {peRange[0]} - {peRange[1]}
                          </span>
                        </div>
                        <div className="px-1 pt-3">
                          <Slider
                            value={peRange}
                            onChange={(_, val) => {
                              setPeRange(val as number[]);
                            }}
                            min={0}
                            max={200}
                            step={1}
                            valueLabelDisplay="auto"
                            sx={{
                              color: "#0f172a",
                              "& .MuiSlider-thumb": {
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                height: "20px",
                                width: "20px",
                                border: "2px solid black",
                              },
                              "& .MuiSlider-track": {
                                backgroundColor: "#66A0CD",
                                borderRadius: "10px",
                                height: "6px",
                                width: "100%",
                                borderColor: "#66A0CD",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Professional Table */}
          <Card className="relative overflow-hidden rounded-lg border-slate-200 bg-white shadow-sm">
            {/* Loading Overlay */}
            {loading && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                <LoadingSpinner
                  text="Loading companies..."
                  fullScreen={false}
                />
              </div>
            )}

            <div className="overflow-x-auto">
              <AntDTable
                data={filteredCompanies}
                columns={columns}
                rowKey="id"
                pagination={false}
                className="screener-table-new"
                onRow={(record: Company) => ({
                  onClick: () =>
                    navigate(
                      screenerNavigationRoutes.COMPANY_DETAILS(record.id)
                    ),
                  className:
                    "group hover:bg-slate-50 border-b border-slate-200 transition-colors cursor-pointer",
                })}
                locale={{
                  emptyText: (
                    <div className="flex flex-col items-center justify-center py-24 text-slate-300">
                      <Search className="mb-4 h-10 w-10 opacity-50" />
                      <p className="text-sm font-bold uppercase tracking-widest">
                        No matching results found
                      </p>
                    </div>
                  ),
                }}
              />
            </div>

            {/* Pagination Footer */}
            <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Total Companies:{" "}
                <span className="text-slate-600">{totalCount}</span>
              </p>
              {totalPages > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="shadow-xs h-9 rounded-md border-slate-200 px-4 text-sm font-bold text-slate-500 transition-all hover:bg-white"
                    disabled={currentPage === 1 || loading}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                  </Button>
                  <div className="flex gap-1">
                    {getPaginationPages().map(page => {
                      if (page === "...") {
                        return (
                          <span
                            key={`ellipsis-${page}`}
                            className="flex h-9 w-9 items-center justify-center text-xs font-bold text-slate-400"
                          >
                            ...
                          </span>
                        );
                      }
                      const pageNum = page as number;
                      return (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum ? "secondary" : "ghost"
                          }
                          size="sm"
                          className={`h-9 w-9 rounded-md text-sm font-bold ${
                            currentPage === pageNum
                              ? "bg-slate-200 text-slate-800"
                              : "text-slate-400 hover:bg-slate-100"
                          }`}
                          disabled={loading}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shadow-xs h-9 rounded-md border-slate-200 px-4 text-sm font-bold text-slate-500 transition-all hover:bg-white"
                    disabled={currentPage === totalPages || loading}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </ProductLayout>
  );
};

export default ScreenerHomePage;
